import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, AlertTriangle, User, Building2, Ship, Plane, ChevronDown, ChevronUp, X } from 'lucide-react'
import { apiClient } from '@/lib/api'
import type { SanctionsStats, SanctionsSearchResult, SanctionEntity } from '@/lib/api'

const TYPE_ICON: Record<string, typeof User> = {
  Individual: User,
  Entity: Building2,
  Vessel: Ship,
  Aircraft: Plane,
}

const TYPE_COLOR: Record<string, string> = {
  Individual: 'text-blue-400',
  Entity: 'text-orange-400',
  Vessel: 'text-cyan-400',
  Aircraft: 'text-purple-400',
}

export default function SanctionsPanel() {
  const [query, setQuery] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [expanded, setExpanded] = useState<number | null>(null)
  const [page, setPage] = useState(1)

  const { data: stats } = useQuery<SanctionsStats>({
    queryKey: ['sanctions-stats'],
    queryFn: () => apiClient.getSanctionsStats(),
    staleTime: 5 * 60_000,
  })

  const { data: results, isLoading } = useQuery<SanctionsSearchResult>({
    queryKey: ['sanctions-search', searchTerm, typeFilter, page],
    queryFn: () => apiClient.searchSanctions({
      q: searchTerm,
      sdn_type: typeFilter || undefined,
      page,
      page_size: 30,
    }),
    enabled: searchTerm.length >= 2 || typeFilter !== '',
    staleTime: 30_000,
  })

  const handleSearch = () => {
    setSearchTerm(query)
    setPage(1)
  }

  return (
    <div className="h-full flex flex-col text-xs font-mono">
      {/* Header stats */}
      {stats && stats.total > 0 && (
        <div className="px-3 py-2 border-b border-gray-800/50 space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 text-orange-400" />
              <span className="text-[9px] text-gray-500 uppercase tracking-widest">OFAC SDN List</span>
            </div>
            <span className="text-[9px] text-gray-600">{stats.publish_date}</span>
          </div>
          <div className="grid grid-cols-4 gap-1">
            {Object.entries(stats.by_type).map(([type, count]) => {
              const Icon = TYPE_ICON[type] || Building2
              return (
                <button
                  key={type}
                  onClick={() => { setTypeFilter(typeFilter === type ? '' : type); setSearchTerm(searchTerm || ' '); setPage(1) }}
                  className={`flex flex-col items-center gap-0.5 px-1.5 py-1 rounded border transition-colors ${
                    typeFilter === type
                      ? 'border-orange-500/50 bg-orange-500/10'
                      : 'border-gray-800/50 hover:border-gray-700'
                  }`}
                >
                  <Icon className={`w-3 h-3 ${TYPE_COLOR[type] || 'text-gray-400'}`} />
                  <span className="text-[9px] text-gray-300">{count.toLocaleString()}</span>
                  <span className="text-[8px] text-gray-600">{type}s</span>
                </button>
              )
            })}
          </div>
          {stats.top_programs.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {stats.top_programs.slice(0, 8).map((p) => (
                <span
                  key={p.program}
                  className="text-[8px] px-1.5 py-0.5 rounded bg-gray-800/60 text-gray-500 cursor-pointer hover:text-orange-400 hover:bg-orange-500/10 transition-colors"
                  onClick={() => { setQuery(p.program); setSearchTerm(p.program); setPage(1) }}
                >
                  {p.program} ({p.count})
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="px-3 py-2 border-b border-gray-800/50">
        <div className="flex gap-1.5">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-600" />
            <input
              className="w-full bg-gray-900 border border-gray-800 rounded pl-7 pr-2 py-1.5 text-xs text-gray-200 placeholder-gray-600 focus:border-orange-500/50 focus:outline-none font-mono"
              placeholder="Search entities, persons, vessels..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-3 py-1.5 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded text-[10px] uppercase tracking-wider hover:bg-orange-500/30 transition-colors"
          >
            Search
          </button>
        </div>
        {typeFilter && (
          <div className="mt-1.5 flex items-center gap-1">
            <span className="text-[9px] text-gray-600">Filter:</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400 border border-orange-500/30 flex items-center gap-1">
              {typeFilter}
              <X className="w-2.5 h-2.5 cursor-pointer" onClick={() => setTypeFilter('')} />
            </span>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
          </div>
        )}

        {results && results.results.length === 0 && (
          <div className="text-center py-8 text-gray-600">No results found</div>
        )}

        {results && results.results.map((entity) => (
          <EntityCard
            key={entity.uid}
            entity={entity}
            expanded={expanded === entity.uid}
            onToggle={() => setExpanded(expanded === entity.uid ? null : entity.uid)}
          />
        ))}

        {/* Pagination */}
        {results && results.total > 30 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-800/50">
            <span className="text-[9px] text-gray-600">{results.total.toLocaleString()} results</span>
            <div className="flex gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="px-2 py-0.5 text-[9px] rounded bg-gray-800 text-gray-400 disabled:opacity-30"
              >
                Prev
              </button>
              <span className="text-[9px] text-gray-500 px-1">{page}</span>
              <button
                disabled={!results.has_more}
                onClick={() => setPage(p => p + 1)}
                className="px-2 py-0.5 text-[9px] rounded bg-gray-800 text-gray-400 disabled:opacity-30"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {!searchTerm && !typeFilter && stats && stats.total > 0 && (
          <div className="px-3 py-4 text-center space-y-2">
            <AlertTriangle className="w-5 h-5 text-orange-500/40 mx-auto" />
            <p className="text-[10px] text-gray-500">
              {stats.total.toLocaleString()} sanctioned entities loaded
            </p>
            <p className="text-[9px] text-gray-600">
              Search by name, country, or program to explore the OFAC SDN list
            </p>
          </div>
        )}

        {stats && stats.total === 0 && (
          <div className="px-3 py-4 text-center space-y-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500/40 mx-auto" />
            <p className="text-[10px] text-gray-500">OFAC data loading...</p>
            <p className="text-[9px] text-gray-600">Initial fetch of ~18,700 entities is in progress</p>
          </div>
        )}
      </div>
    </div>
  )
}

function EntityCard({ entity, expanded, onToggle }: { entity: SanctionEntity; expanded: boolean; onToggle: () => void }) {
  const Icon = TYPE_ICON[entity.sdn_type] || Building2
  const color = TYPE_COLOR[entity.sdn_type] || 'text-gray-400'

  return (
    <div className="border-b border-gray-800/30 hover:bg-gray-900/50 transition-colors">
      <button className="w-full px-3 py-2 text-left flex items-start gap-2" onClick={onToggle}>
        <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-200 font-semibold truncate">{entity.name}</span>
            <span className="text-[8px] text-gray-600 uppercase shrink-0">{entity.sdn_type}</span>
          </div>
          <div className="flex flex-wrap gap-1 mt-0.5">
            {entity.programs.slice(0, 3).map((p) => (
              <span key={p} className="text-[8px] px-1 py-0 rounded bg-red-500/10 text-red-400 border border-red-500/20">
                {p}
              </span>
            ))}
            {entity.countries.slice(0, 2).map((c) => (
              <span key={c} className="text-[8px] px-1 py-0 rounded bg-gray-800 text-gray-500">
                {c}
              </span>
            ))}
          </div>
        </div>
        {expanded ? <ChevronUp className="w-3 h-3 text-gray-600 shrink-0" /> : <ChevronDown className="w-3 h-3 text-gray-600 shrink-0" />}
      </button>

      {expanded && (
        <div className="px-3 pb-2 pl-8 space-y-1.5 text-[10px]">
          {entity.aliases.length > 0 && (
            <div>
              <span className="text-gray-600 uppercase text-[8px]">Aliases:</span>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {entity.aliases.map((a, i) => (
                  <span key={i} className="text-gray-400">{a.name}{a.strength === 'weak' ? ' (weak)' : ''}</span>
                ))}
              </div>
            </div>
          )}
          {entity.addresses.length > 0 && (
            <div>
              <span className="text-gray-600 uppercase text-[8px]">Addresses:</span>
              {entity.addresses.map((a, i) => (
                <div key={i} className="text-gray-400">
                  {[a.address1, a.address2, a.city, a.stateOrProvince, a.postalCode, a.country].filter(Boolean).join(', ')}
                </div>
              ))}
            </div>
          )}
          {entity.ids.length > 0 && (
            <div>
              <span className="text-gray-600 uppercase text-[8px]">IDs:</span>
              {entity.ids.map((id, i) => (
                <div key={i} className="text-gray-400">{id.idType}: {id.idNumber}{id.idCountry ? ` (${id.idCountry})` : ''}</div>
              ))}
            </div>
          )}
          {entity.date_of_birth && <div className="text-gray-400">DOB: {entity.date_of_birth}</div>}
          {entity.place_of_birth && <div className="text-gray-400">POB: {entity.place_of_birth}</div>}
          {entity.remarks && <div className="text-gray-500 italic leading-snug">{entity.remarks}</div>}
        </div>
      )}
    </div>
  )
}
